import { useState } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authLinkSx, authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';

import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import {
  resolvePostLoginDestination,
  registeredSignInNotice,
  withAppQuery,
  buildCheckEmailPath,
} from '@utils/authRouting';
import api from '@services/api';
import { brandColors } from '@config/brandTokens';
import { rememberAddChildNext } from '@utils/parentalConsent';

const REGISTER_OPTIONS = [
  {
    label: 'Player / Parent',
    kind: 'Player account',
    description: 'Create your player account. After sign-in you can add a child from Profile.',
    path: '/register/player',
  },
  {
    label: 'Coach',
    kind: 'Coach account',
    description: 'Register as a coach. You also get a player profile for contests and self-eval. More coach tools come later.',
    path: '/register/coach',
  },
  {
    label: 'School, College or Sports Academy',
    kind: 'Academy account',
    description: 'Register your school, college, or sports academy on EncycGames.',
    path: '/register/academy',
  },
  {
    label: 'Corporate / employer',
    kind: 'Corporate account',
    description: 'Register your company. Employees map themselves and appear on your public profile.',
    path: '/register/academy?type=corporate',
  },
  {
    label: 'Sponsors & Organizers',
    kind: 'Partnership enquiry',
    description: 'Contest and event partnership — not an academy account.',
    path: '/partners',
  },
];

// Validation schema - accepts email, player ID, or coach ID
const loginSchema = z.object({
  email: z.string().min(1, 'Email, Player ID, Coach ID, or Admin ID is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const returnUrl = searchParams.get('returnUrl');
  const registeredNotice = registeredSignInNotice(searchParams.get('registered'), returnUrl);
  const prefillEmail = String(searchParams.get('email') || '').trim();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: prefillEmail, password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');
    setUnverifiedEmail('');

    try {
      console.log('LoginPage - Attempting login with:', { email: data.email });
      const response = await authService.login(data);
      console.log('LoginPage - Login response received:', response);
      console.log('LoginPage - User object:', response.user);
      if (!response.user) {
        throw new Error('Sign in failed. Please try again.');
      }
      console.log('LoginPage - User academyId:', response.user.academyId);
      login(response.user);
      toast.success('Signed in successfully.');

      if (returnUrl?.includes('addChild')) {
        rememberAddChildNext();
      }

      navigate(resolvePostLoginDestination(response.user, returnUrl));
    } catch (err: any) {
      console.error('LoginPage - Login error:', err);
      console.error('LoginPage - Error response:', err.response);
      console.error('LoginPage - Error message:', err.message);
      console.error('LoginPage - Error data:', err.response?.data);
      const data = err.response?.data || {};
      const errorMessage = data.message || err.message || 'Sign in failed. Please try again.';
      setError(errorMessage);
      if (data.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(String(data.email || data.emailAddress || '').trim());
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell>
      <AuthFormCard
        roleLabel="Sign in"
        title="Sign in to your account"
        subtitle="Use your email, Player ID, Coach ID, or Admin ID."
        chrome
      >
        {registeredNotice && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {registeredNotice}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 1 }}>
          {returnUrl?.includes('addChild') ? (
            <>
              Sign in with <strong>your email</strong>, then add your child — that is the first task on Profile.
            </>
          ) : (
            <>
              Parents: sign in with <strong>your email</strong> to use your own Player ID (learn, score, rank) and
              manage children. A child signs in with their <strong>Player ID</strong> (not your email). After you add
              a child, confirm the verification email we sent to your login — they cannot sign in until that link
              is confirmed. Schools, colleges, academies, and corporates sign in with an{' '}
              <strong>Admin ID</strong>, not the contact email.
            </>
          )}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2, ...authLinkSx }}>
            {error}
            {unverifiedEmail && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <Link
                  component={RouterLink}
                  to={buildCheckEmailPath({ email: unverifiedEmail, returnUrl })}
                >
                  Open the check-your-email page
                </Link>
                {' · '}
                <Link
                  component="button"
                  type="button"
                  onClick={async () => {
                    try {
                      await api.post('/auth/send-email-verification', { email: unverifiedEmail });
                      toast.success('If that inbox exists, we sent another verification link.');
                    } catch (resendErr: any) {
                      toast.error(resendErr.response?.data?.message || 'Could not resend.');
                    }
                  }}
                >
                  Resend verification email
                </Link>
              </Typography>
            )}
            {error.toLowerCase().includes('parental consent') && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Check the parent email for the confirmation link, then try again. The child cannot sign in until
                parental consent is confirmed. After that, they sign in with this Player ID and the family password
                (your email password), unless you set a separate child password.
              </Typography>
            )}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
          <TextField
            {...register('email')}
            label="Email, Player ID, Coach ID, or Admin ID"
            fullWidth
            margin="normal"
            autoComplete="email"
            autoFocus
            error={!!errors.email}
            helperText={errors.email?.message}
          />

          <TextField
            {...register('password')}
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            autoComplete="current-password"
            error={!!errors.password}
            helperText={errors.password?.message}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2, ...authPrimaryCtaSx }}
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>

          <Box sx={{ mt: 1, ...authLinkSx }}>
            <Link component={RouterLink} to="/forgot-password" sx={{ display: 'block', mb: 2.5 }}>
              <Typography variant="body2" sx={{ color: brandColors.blue }}>
                Forgot Password?
              </Typography>
            </Link>
            <Typography
              variant="subtitle2"
              sx={{
                color: brandColors.navy,
                fontWeight: 800,
                fontFamily: '"Sora", Arial, sans-serif',
                mb: 1,
              }}
            >
              Don&apos;t have an account? Register as
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {REGISTER_OPTIONS.map((option) => (
                <Box
                  component="li"
                  key={option.path}
                  sx={{
                    borderTop: '1px solid #e2e8f0',
                    '&:last-of-type': { borderBottom: '1px solid #e2e8f0' },
                  }}
                >
                  <Link
                    component={RouterLink}
                    to={withAppQuery(option.path, { returnUrl })}
                    underline="none"
                    sx={{
                      display: 'block',
                      py: 1.25,
                      '&:hover': { bgcolor: brandColors.ice },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: brandColors.slate,
                        fontWeight: 800,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        mb: 0.25,
                      }}
                    >
                      {option.kind}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: brandColors.blue, lineHeight: 1.3 }}>
                      {option.label}
                    </Typography>
                    <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.25, lineHeight: 1.4 }}>
                      {option.description}
                    </Typography>
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </AuthFormCard>
    </AuthPageShell>
  );
};

export default LoginPage;


